import { ValidationErrors, ValidatorFn } from '@angular/forms';
import { AbstractControlWithWarning } from '.';
import { SENSORS_HCPCS_GROUP, SETS_RESERVOIRS_HCPCS_GROUP, TESTING_SUPPLIES_HCPCS_GROUP } from '../../constants';
import { ReorderDetailsItem } from '../../models';

interface QuantityValidationResult {
  quantityToSubmit: number;
  title: string | null;
  message: string;
}

const validateForMaximumQuantity = (
  item: ReorderDetailsItem,
  userQuantity: number,
  ccsConfig: any
): QuantityValidationResult => {
  const quantityToSubmit = item.submitInvalidQuantity ? userQuantity : item.quantity!;
  const title = item.submitInvalidQuantity
    ? null
    : `Requested amount was Qty: ${userQuantity} but can only fullfill Qty: ${item.maxValidShippingQuantity}`;
  let message = 'Maximum allowed quantity exceeded.';

  switch (item.hcpcsGroup) {
    case SETS_RESERVOIRS_HCPCS_GROUP: {
      if (item.isMedicareOrMedicareRepl) {
        // Message 1
        message = `Your requested quantity exceeds the eligible quantity of ${item.maxValidShippingQuantity}. If you require more supplies, please have your provider fax your clinical notes to <a href="tel:${ccsConfig.helpFax}">${ccsConfig.helpFax}</a>.
        Please e-mail us at <a href="mailto:${ccsConfig.helpEmail}">${ccsConfig.helpEmail}</a> when you are down to 10 days' supply on hand.
        If we receive valid documentation that supports the increased quantity then we can ship out the balance of your order.`;
      } else {
        // Message 2
        message = `Your requested quantity exceeds the quantity allowed of ${item.maxValidShippingQuantity}. We can send you the allowed quantity at this time so there is no interruption in your supplies. We will contact your provider for an updated written order to cover the increased quantity.
        Please e-mail us at <a href="mailto:${ccsConfig.helpEmail}">${ccsConfig.helpEmail}</a> when you are down to 10 days' supply on hand.`;
      }
      break;
    }
    case SENSORS_HCPCS_GROUP: {
      // Message 3
      message = `Your requested quantity of sensors exceeds the allowed amount of ${item.maxValidShippingQuantity}. Sensors are FDA approved and guaranteed to last the recommended days by the manufacturer. The manufacturer is required to report issues with sensors that do not last this timeframe to the FDA. You can call the manufacturer's technical support line. Most insurance companies will not cover site changes more than the FDA approved/manufacturer recommended site change.
      If your documentation supports the increased quantity change frequency then we can ship out the balance of your order.`;
      break;
    }
    case TESTING_SUPPLIES_HCPCS_GROUP: {
      // Message 4
      message = `The quantity you have indicated exceeds your current testing frequency. We can send you the allowed quantity at this time so there is no interruption in your supplies. We will contact your provider for an updated provider's written order to cover the increased quantity. Please e-mail us at <a href="mailto:${ccsConfig.helpEmail}">${ccsConfig.helpEmail}</a> when you are down to 10 day's supply on hand.`;
      break;
    }
  }

  return { quantityToSubmit, title, message };
};

const validateForMinimumQuantity = (
  item: ReorderDetailsItem,
  userQuantity: number,
  ccsConfig: any
): QuantityValidationResult => {
  const quantityToSubmit = item.submitInvalidQuantity ? userQuantity : item.quantity!;
  const title = item.submitInvalidQuantity
    ? null
    : `Requested amount was Qty: ${userQuantity} but can fullfill up to Qty: ${item.maxValidShippingQuantity}`;
  let message = 'Minimum allowed quantity not met.';

  switch (item.hcpcsGroup) {
    case SETS_RESERVOIRS_HCPCS_GROUP: {
      if (item.isMedicareOrMedicareRepl) {
        // Message 5
        message = `Medicare only allows billing one time every 90 days. You are authorized to receive the allowed quantity ${item.minValidShippingQuantity} of infusion sets indicated. If you reduce the quantity at this time, you may run the risk of running out of supplies before your next order.`;
      }
      break;
    }
    case TESTING_SUPPLIES_HCPCS_GROUP: {
      // Message 6
      message =
        'Has your provider authorized the decrease in your testing frequency? Are you testing a new frequency? If you answered "No" to either question then you should know that testing your blood sugar regularly is one of the best ways to stay in control of your diabetes. Your test results help you and your provider create a treatment program designed for your specific diabetes needs.';
      break;
    }
  }

  return { quantityToSubmit, title, message };
};

export function itemQuantityValidator(item: ReorderDetailsItem, ccsConfig: any): ValidatorFn {
  return (control: AbstractControlWithWarning): ValidationErrors | null => {
    if (!control.value && control.value !== 0) return null;

    const userQuantity = control.value;

    if (item.minValidShippingQuantity && userQuantity < item.minValidShippingQuantity) {
      control.warnings = { invalidValue: validateForMinimumQuantity(item, userQuantity, ccsConfig) };
    } else if (item.maxValidShippingQuantity && userQuantity > item.maxValidShippingQuantity) {
      control.warnings = { invalidValue: validateForMaximumQuantity(item, userQuantity, ccsConfig) };
    } else {
      control.warnings = null;
    }
    return null;
  };
}
